/**
 * HubSpot dashboards (DWA Internal → Growth).
 *
 * Five Growth Hub items share this view, one `section` each, fed by a single
 * GET /dashboard/dwa-internal/hubspot payload (services/agent/app/hubspot_dashboard.py,
 * where every metric is defined). Data is a read-only, current-state copy of
 * DWA's own HubSpot, refreshed by the HubSpot sync (KB hubspot.md): on demand
 * via "Sync now" (admins), plus daily on prod once HUBSPOT_SYNC_ENABLED is on.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  fetchHubSpotDashboard,
  triggerHubSpotSync,
  type HubSpotActivityType,
  type HubSpotDashboardResponse,
  type HubSpotDealBrief,
  type HubSpotHygieneCheck,
} from '../services/dashboardApi';
import { useAuth } from '../contexts/AuthContext';
import type { ScorecardTheme } from './ScorecardView';

export type HubSpotSection = 'pipeline' | 'leads' | 'win-loss' | 'owners' | 'health';

const TITLES: Record<HubSpotSection, { title: string; subtitle: string }> = {
  pipeline: { title: 'Deal Pipeline', subtitle: 'Open deals by stage, weighted forecast, closing soon and stalled deals.' },
  leads: { title: 'New Leads', subtitle: 'New contacts and companies per week, by original source and lifecycle stage.' },
  'win-loss': { title: 'Win / Loss', subtitle: 'Deals won and lost by close month, win rate, deal size and sales cycle.' },
  owners: { title: 'Owner Activity', subtitle: 'Pipeline, wins, new deals and logged activity per deal owner.' },
  health: { title: 'Lifecycle & CRM Hygiene', subtitle: 'Where contacts sit in the lifecycle, and the records that need fixing.' },
};

const AMBER = '#f59e0b';
const STALLED_OPTIONS = [14, 30, 60, 90];

// ─── formatting ───────────────────────────────────────────────────────────

const usd = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const num = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-US'));
const pct = (n: number | null | undefined) => (n == null ? '—' : `${Math.round(n * 1000) / 10}%`);

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

const shortWeek = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

// ─── pieces ───────────────────────────────────────────────────────────────

const Card: React.FC<{ theme: ScorecardTheme; title?: string; subtitle?: string; children: React.ReactNode }> = ({
  theme, title, subtitle, children,
}) => (
  <div style={{
    background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, boxShadow: theme.cardShadow,
    borderRadius: 4, padding: '1rem 1.15rem', minWidth: 0,
  }}>
    {title && (
      <div style={{ marginBottom: '0.6rem' }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.sectionTitle }}>
          {title}
        </div>
        {subtitle && <div style={{ fontSize: '0.74rem', color: theme.textMuted, marginTop: '0.15rem' }}>{subtitle}</div>}
      </div>
    )}
    {children}
  </div>
);

const Kpi: React.FC<{ theme: ScorecardTheme; label: string; value: string; sub?: string; color?: string }> = ({
  theme, label, value, sub, color,
}) => (
  <Card theme={theme}>
    <div style={{ fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.textMuted }}>{label}</div>
    <div style={{ fontSize: '1.35rem', fontWeight: 700, color: color ?? theme.textPrimary, marginTop: '0.2rem' }}>{value}</div>
    {sub && <div style={{ fontSize: '0.72rem', color: theme.textMuted, marginTop: '0.1rem' }}>{sub}</div>}
  </Card>
);

const KpiRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.85rem' }}>{children}</div>
);

interface Col<T> {
  label: string;
  render: (row: T) => React.ReactNode;
  align?: 'left' | 'right';
}

function Table<T>({ theme, cols, rows, rowKey, empty }: {
  theme: ScorecardTheme; cols: Col<T>[]; rows: T[]; rowKey: (r: T, i: number) => string; empty?: string;
}) {
  const th: React.CSSProperties = {
    fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
    color: theme.textMuted, padding: '0.35rem 0.5rem', borderBottom: `1px solid ${theme.rowDivider}`, whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = {
    fontSize: '0.8rem', color: theme.textPrimary, padding: '0.4rem 0.5rem', borderBottom: `1px solid ${theme.rowDivider}`,
  };
  if (!rows.length) return <div style={{ fontSize: '0.82rem', color: theme.textMuted }}>{empty ?? 'Nothing to show.'}</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>{cols.map((c) => <th key={c.label} style={{ ...th, textAlign: c.align ?? 'left' }}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={rowKey(r, i)}>
              {cols.map((c) => <td key={c.label} style={{ ...td, textAlign: c.align ?? 'left' }}>{c.render(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function tooltipStyle(theme: ScorecardTheme): React.CSSProperties {
  return { background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, fontSize: 12, borderRadius: 3 };
}

const ChartBox: React.FC<{ children: React.ReactElement; height?: number }> = ({ children, height = 240 }) => (
  <div style={{ height }}>
    <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
  </div>
);

const ISSUE_LABEL: Record<HubSpotHygieneCheck, string> = {
  missing_amount: 'No amount',
  missing_close_date: 'No close date',
  missing_owner: 'No owner',
  past_due: 'Close date passed',
  no_company: 'No company',
};

function dealCols(theme: ScorecardTheme, extra: Col<HubSpotDealBrief>[] = []): Col<HubSpotDealBrief>[] {
  return [
    { label: 'Deal', render: (d) => <span style={{ fontWeight: 600 }}>{d.name}</span> },
    { label: 'Stage', render: (d) => <span style={{ color: theme.textSecondary }}>{d.stage ?? '—'}</span> },
    { label: 'Owner', render: (d) => d.owner },
    { label: 'Amount', align: 'right', render: (d) => usd(d.amount) },
    { label: 'Close date', render: (d) => fmtDate(d.close_date) },
    ...extra,
  ];
}

// ─── sections ─────────────────────────────────────────────────────────────

const PipelineSection: React.FC<{ data: HubSpotDashboardResponse; theme: ScorecardTheme; stalledDays: number; setStalledDays: (n: number) => void }> = ({
  data, theme, stalledDays, setStalledDays,
}) => {
  const p = data.pipeline;
  return (
    <>
      <KpiRow>
        <Kpi theme={theme} label="Open pipeline" value={usd(p.totals.amount)} sub={`${num(p.totals.count)} open deals`} />
        <Kpi theme={theme} label="Weighted forecast" value={usd(p.totals.weighted)} sub="amount × stage probability" />
        <Kpi theme={theme} label="Closing this month" value={usd(p.closing_this_month.amount)}
          sub={`${num(p.closing_this_month.count)} deals · ${usd(p.closing_this_month.weighted)} weighted`} />
        <Kpi theme={theme} label="Closing this quarter" value={usd(p.closing_this_quarter.amount)}
          sub={`${num(p.closing_this_quarter.count)} deals · ${usd(p.closing_this_quarter.weighted)} weighted`} />
        <Kpi theme={theme} label={`Stalled ≥ ${p.stalled.days} days`} value={num(p.stalled.count)}
          sub={usd(p.stalled.amount)} color={p.stalled.count ? AMBER : undefined} />
      </KpiRow>

      {p.pipelines.map((pipe) => (
        <Card key={pipe.pipeline_id} theme={theme} title={pipe.label}
          subtitle={`${num(pipe.count)} open deals · ${usd(pipe.amount)} · ${usd(pipe.weighted)} weighted`}>
          {pipe.stages.length > 0 && (
            <ChartBox height={220}>
              <BarChart data={pipe.stages} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
                <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" stroke={theme.textMuted} tick={{ fontSize: 11 }} tickLine={false} />
                <YAxis stroke={theme.textMuted} tick={{ fontSize: 11 }} tickLine={false}
                  tickFormatter={(v: number) => `$${Math.round(v / 1000)}k`} />
                <Tooltip contentStyle={tooltipStyle(theme)} formatter={(v) => usd(Number(v))} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="amount" name="Amount" fill={theme.accent} />
                <Bar dataKey="weighted" name="Weighted" fill={theme.highlight ?? '#0ea5e9'} />
              </BarChart>
            </ChartBox>
          )}
          <Table theme={theme} rowKey={(s, i) => `${s.stage_id ?? 'unknown'}-${i}`} rows={pipe.stages} cols={[
            { label: 'Stage', render: (s) => s.label },
            { label: 'Probability', align: 'right', render: (s) => pct(s.probability) },
            { label: 'Deals', align: 'right', render: (s) => num(s.count) },
            { label: 'Amount', align: 'right', render: (s) => usd(s.amount) },
            { label: 'Weighted', align: 'right', render: (s) => usd(s.weighted) },
          ]} />
        </Card>
      ))}

      <Card theme={theme} title="Stalled deals"
        subtitle="Open deals that entered their current stage at least this many days ago (falls back to last modified).">
        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          {STALLED_OPTIONS.map((n) => (
            <button key={n} type="button" onClick={() => setStalledDays(n)} style={{
              fontSize: '0.74rem', fontWeight: 600, padding: '0.25rem 0.6rem', borderRadius: 4, cursor: 'pointer',
              fontFamily: 'inherit', border: `1px solid ${theme.cardBorder}`,
              background: n === stalledDays ? theme.textPrimary : 'transparent',
              color: n === stalledDays ? theme.cardBg : theme.textSecondary,
            }}>{n} days</button>
          ))}
        </div>
        <Table theme={theme} rows={p.stalled.deals} rowKey={(d) => d.deal_id} empty="No stalled deals."
          cols={dealCols(theme, [
            { label: 'Days in stage', align: 'right', render: (d) => num(d.days_in_stage) },
            { label: 'Last touched', align: 'right', render: (d) => (d.days_since_modified == null ? '—' : `${d.days_since_modified}d ago`) },
          ])} />
      </Card>
    </>
  );
};

const LeadsSection: React.FC<{ data: HubSpotDashboardResponse; theme: ScorecardTheme }> = ({ data, theme }) => {
  const l = data.leads;
  const weeks = l.weeks.map((w) => ({ ...w, label: shortWeek(w.week_start) }));
  return (
    <>
      <KpiRow>
        <Kpi theme={theme} label="New contacts" value={num(l.totals.contacts)} sub={`since ${fmtDate(l.since)}`} />
        <Kpi theme={theme} label="New companies" value={num(l.totals.companies)} sub={`since ${fmtDate(l.since)}`} />
        <Kpi theme={theme} label="Contacts / week" value={num(Math.round((l.totals.contacts / Math.max(1, l.weeks.length)) * 10) / 10)}
          sub={`average over ${l.weeks.length} weeks`} />
      </KpiRow>
      <Card theme={theme} title="New contacts and companies per week" subtitle="Weeks start Monday (UTC), by HubSpot create date.">
        <ChartBox>
          <BarChart data={weeks} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
            <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" stroke={theme.textMuted} tick={{ fontSize: 11 }} tickLine={false} />
            <YAxis stroke={theme.textMuted} tick={{ fontSize: 11 }} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle(theme)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="contacts" name="Contacts" fill={theme.accent} />
            <Bar dataKey="companies" name="Companies" fill={theme.highlight ?? '#0ea5e9'} />
          </BarChart>
        </ChartBox>
      </Card>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
        <Card theme={theme} title="By original source" subtitle="Records created in the last 12 weeks.">
          <Table theme={theme} rows={l.by_source} rowKey={(r) => r.source ?? 'unknown'} empty="No new records."
            cols={[
              { label: 'Source', render: (r) => r.label },
              { label: 'Contacts', align: 'right', render: (r) => num(r.contacts) },
              { label: 'Companies', align: 'right', render: (r) => num(r.companies) },
            ]} />
        </Card>
        <Card theme={theme} title="By lifecycle stage" subtitle="New contacts' current lifecycle stage.">
          <Table theme={theme} rows={l.by_lifecycle} rowKey={(r) => r.stage} empty="No new contacts."
            cols={[
              { label: 'Stage', render: (r) => r.label },
              { label: 'Contacts', align: 'right', render: (r) => num(r.contacts) },
            ]} />
        </Card>
      </div>
    </>
  );
};

const WinLossSection: React.FC<{ data: HubSpotDashboardResponse; theme: ScorecardTheme }> = ({ data, theme }) => {
  const w = data.win_loss;
  const t = w.totals;
  return (
    <>
      <KpiRow>
        <Kpi theme={theme} label="Won" value={num(t.won)} sub={usd(t.won_amount)} color={theme.positive} />
        <Kpi theme={theme} label="Lost" value={num(t.lost)} color={t.lost ? theme.negative : undefined} />
        <Kpi theme={theme} label="Win rate" value={pct(t.win_rate)} sub="won ÷ (won + lost)" />
        <Kpi theme={theme} label="Avg won deal" value={usd(t.avg_won_amount)} />
        <Kpi theme={theme} label="Avg sales cycle" value={t.avg_cycle_days == null ? '—' : `${t.avg_cycle_days} days`}
          sub="create → close, won deals" />
      </KpiRow>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '1rem' }}>
        <Card theme={theme} title="Deals closed per month" subtitle={`By close date, since ${fmtDate(w.since)}.`}>
          <ChartBox>
            <BarChart data={w.months} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
              <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke={theme.textMuted} tick={{ fontSize: 11 }} tickLine={false} />
              <YAxis stroke={theme.textMuted} tick={{ fontSize: 11 }} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle(theme)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="won" name="Won" stackId="a" fill={theme.positive} />
              <Bar dataKey="lost" name="Lost" stackId="a" fill={theme.negative} />
            </BarChart>
          </ChartBox>
        </Card>
        <Card theme={theme} title="Won amount per month">
          <ChartBox>
            <BarChart data={w.months} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
              <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke={theme.textMuted} tick={{ fontSize: 11 }} tickLine={false} />
              <YAxis stroke={theme.textMuted} tick={{ fontSize: 11 }} tickLine={false}
                tickFormatter={(v: number) => `$${Math.round(v / 1000)}k`} />
              <Tooltip contentStyle={tooltipStyle(theme)} formatter={(v) => usd(Number(v))} />
              <Bar dataKey="won_amount" name="Won amount" fill={theme.accent} />
            </BarChart>
          </ChartBox>
        </Card>
      </div>
    </>
  );
};

const ACTIVITY_LABEL: Record<HubSpotActivityType, string> = {
  calls: 'Calls', meetings: 'Meetings', emails: 'Emails', notes: 'Notes', tasks: 'Tasks',
};

const OwnersSection: React.FC<{ data: HubSpotDashboardResponse; theme: ScorecardTheme }> = ({ data, theme }) => {
  const o = data.owners;
  const types = (Object.keys(ACTIVITY_LABEL) as HubSpotActivityType[]).filter((t) => !data.activity_unavailable.includes(t));
  const cols: Col<(typeof o.owners)[number]>[] = [
    { label: 'Owner', render: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { label: 'Open deals', align: 'right', render: (r) => num(r.open_count) },
    { label: 'Open pipeline', align: 'right', render: (r) => usd(r.open_amount) },
    { label: 'Weighted', align: 'right', render: (r) => usd(r.weighted) },
    { label: 'Won this quarter', align: 'right', render: (r) => `${num(r.won_quarter_count)} · ${usd(r.won_quarter_amount)}` },
    { label: 'Created this month', align: 'right', render: (r) => num(r.created_month) },
  ];
  if (o.activity_available) {
    for (const t of types) cols.push({ label: ACTIVITY_LABEL[t], align: 'right', render: (r) => num(r.activities[t]) });
  }
  return (
    <Card theme={theme} title="Per deal owner"
      subtitle={o.activity_available
        ? `Activity = records logged in HubSpot in the last ${o.activity_days} days.`
        : 'Activity counts are unavailable: the HubSpot token lacks the scopes to read calls, meetings, emails, notes or tasks.'}>
      <Table theme={theme} rows={o.owners} rowKey={(r) => r.owner_id ?? 'unassigned'} cols={cols} empty="No owners yet." />
      {o.activity_available && data.activity_unavailable.length > 0 && (
        <div style={{ fontSize: '0.74rem', color: theme.textMuted, marginTop: '0.6rem' }}>
          Not readable with the current token's scopes: {data.activity_unavailable.map((t) => ACTIVITY_LABEL[t]).join(', ')}.
        </div>
      )}
    </Card>
  );
};

const HealthSection: React.FC<{ data: HubSpotDashboardResponse; theme: ScorecardTheme }> = ({ data, theme }) => {
  const f = data.funnel;
  const h = data.hygiene;
  // Start the funnel at the first stage anyone is in (most portals skip Subscriber).
  const firstUsed = f.stages.findIndex((s) => s.count > 0);
  const stages = firstUsed < 0 ? [] : f.stages.slice(firstUsed).map((s, i) => ({ ...s, conversion: i === 0 ? null : s.conversion }));
  const checks = Object.keys(ISSUE_LABEL) as HubSpotHygieneCheck[];
  return (
    <>
      <Card theme={theme} title="Lifecycle funnel"
        subtitle="Contacts at each stage now, and how many reached at least that stage (lifecycle moves forward only). Conversion = reached ÷ reached at the stage before.">
        {stages.length > 0 && (
          <ChartBox height={220}>
            <BarChart data={stages} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
              <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke={theme.textMuted} tick={{ fontSize: 11 }} tickLine={false} />
              <YAxis stroke={theme.textMuted} tick={{ fontSize: 11 }} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle(theme)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="reached" name="Reached stage" fill={theme.accent} />
              <Bar dataKey="count" name="At stage now" fill={theme.highlight ?? '#0ea5e9'} />
            </BarChart>
          </ChartBox>
        )}
        <Table theme={theme} rows={stages} rowKey={(s) => s.stage} empty="No contacts with a lifecycle stage."
          cols={[
            { label: 'Stage', render: (s) => s.label },
            { label: 'At stage now', align: 'right', render: (s) => num(s.count) },
            { label: 'Reached', align: 'right', render: (s) => num(s.reached) },
            { label: 'Conversion', align: 'right', render: (s) => pct(s.conversion) },
          ]} />
        {(f.other > 0 || f.none > 0) && (
          <div style={{ fontSize: '0.74rem', color: theme.textMuted, marginTop: '0.6rem' }}>
            Not in the funnel: {num(f.other)} in a custom or "Other" stage, {num(f.none)} with no stage (of {num(f.total)} contacts).
          </div>
        )}
      </Card>

      <KpiRow>
        {checks.map((c) => (
          <Kpi key={c} theme={theme} label={ISSUE_LABEL[c]} value={num(h.deal_checks[c])}
            sub={`of ${num(h.open_deals)} open deals`} color={h.deal_checks[c] ? AMBER : theme.positive} />
        ))}
        <Kpi theme={theme} label="Contacts, no owner" value={num(h.contacts_no_owner)} sub={`of ${num(h.contacts_total)}`}
          color={h.contacts_no_owner ? AMBER : theme.positive} />
        <Kpi theme={theme} label="Companies, no owner" value={num(h.companies_no_owner)} sub={`of ${num(h.companies_total)}`}
          color={h.companies_no_owner ? AMBER : theme.positive} />
      </KpiRow>

      <Card theme={theme} title="Open deals to fix" subtitle="Most issues first.">
        <Table theme={theme} rows={h.deals} rowKey={(d) => d.deal_id} empty="Every open deal has an amount, close date, owner and company."
          cols={dealCols(theme, [
            { label: 'Issues', render: (d) => (d.issues ?? []).map((i) => ISSUE_LABEL[i as HubSpotHygieneCheck] ?? i).join(', ') },
          ])} />
      </Card>
    </>
  );
};

// ─── main view ────────────────────────────────────────────────────────────

export interface HubSpotDashboardProps {
  theme: ScorecardTheme;
  section: HubSpotSection;
}

export const HubSpotDashboardView: React.FC<HubSpotDashboardProps> = ({ theme, section }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'dwa_admin';
  const [data, setData] = useState<HubSpotDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stalledDays, setStalledDays] = useState(30);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const timers = useRef<number[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchHubSpotDashboard(stalledDays));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [stalledDays]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { timers.current.forEach((t) => window.clearTimeout(t)); }, []);

  const sync = async () => {
    setSyncMsg('Queuing sync…');
    try {
      await triggerHubSpotSync();
      setSyncMsg('Sync queued. It usually takes under a minute; this page refreshes itself.');
      // A full sync of DWA's portal is well under a few minutes; re-read a few times.
      timers.current = [20_000, 60_000, 150_000].map((ms) => window.setTimeout(() => { load(); }, ms));
    } catch (e) {
      setSyncMsg(`Could not queue the sync: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const { title, subtitle } = TITLES[section];
  const latest = data?.sync.latest;
  const lastOk = data?.sync.last_success;
  const button: React.CSSProperties = {
    fontSize: '0.78rem', fontWeight: 600, padding: '0.4rem 0.85rem', borderRadius: 4, cursor: 'pointer',
    fontFamily: 'inherit', background: 'transparent', color: theme.textSecondary, border: `1px solid ${theme.cardBorder}`,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.textPrimary, margin: 0 }}>{title}</h2>
          <p style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.25rem' }}>
            {subtitle} From DWA's HubSpot.
            {lastOk && <> · Last synced {fmtDateTime(lastOk.finished_at ?? lastOk.started_at)}</>}
            {lastOk?.status === 'partial' && <span style={{ color: AMBER }}> (partial)</span>}
            {latest?.status === 'running' && <span style={{ color: AMBER }}> · sync running</span>}
            {latest?.status === 'failed' && <span style={{ color: theme.negative }}> · last sync failed</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {isAdmin && <button type="button" onClick={sync} style={button}>Sync now</button>}
          <button type="button" onClick={load} disabled={loading} style={{ ...button, opacity: loading ? 0.6 : 1, cursor: loading ? 'default' : 'pointer' }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {syncMsg && <div style={{ fontSize: '0.8rem', color: theme.textSecondary }}>{syncMsg}</div>}

      {error && (
        <div style={{ background: theme.cardBg, border: `1px solid ${theme.negative}`, borderRadius: 4, padding: '1rem 1.25rem', color: theme.negative, fontSize: '0.85rem' }}>
          Failed to load HubSpot data: {error}
        </div>
      )}

      {!error && data && !data.has_data && (
        <Card theme={theme}>
          <div style={{ textAlign: 'center', padding: '1.5rem 0.5rem', color: theme.textSecondary, fontSize: '0.88rem', lineHeight: 1.55 }}>
            <div style={{ fontWeight: 700, color: theme.textPrimary, fontSize: '1rem', marginBottom: '0.4rem' }}>No HubSpot data yet</div>
            {latest?.status === 'skipped'
              ? <>The last sync was skipped: no HubSpot access token is configured (Key Vault secret <code>hubspot-access-token</code>).</>
              : <>The HubSpot sync hasn't run in this environment yet.</>}
            <div style={{ marginTop: '0.5rem' }}>
              {isAdmin
                ? <>Click <strong>Sync now</strong> to pull DWA's HubSpot (read-only). It runs on demand here; on production it also runs daily once enabled.</>
                : <>Ask a DWA admin to run a sync from this page.</>}
            </div>
          </div>
        </Card>
      )}

      {!error && data?.has_data && (
        <>
          {section === 'pipeline' && <PipelineSection data={data} theme={theme} stalledDays={stalledDays} setStalledDays={setStalledDays} />}
          {section === 'leads' && <LeadsSection data={data} theme={theme} />}
          {section === 'win-loss' && <WinLossSection data={data} theme={theme} />}
          {section === 'owners' && <OwnersSection data={data} theme={theme} />}
          {section === 'health' && <HealthSection data={data} theme={theme} />}
        </>
      )}
    </div>
  );
};

export default HubSpotDashboardView;
